import React from 'react';

import { useMe } from '$/hooks/useMe';
import useRunOnce from '$/hooks/useRunOnce';
import Header from '$/sections/Home/Header';
import { ErrorExtensions } from '$/shared/frontend/ErrorExtensions';

import SEO from '../../SEO';
import UhOh from '../../ui/UhOh';
import { UhOhProps } from './types';
import { UhOhPage } from './UhOhPage';
import { getVisitorId } from '$/utils/visitor';
import { logger } from '$/utils/logger';

const NoWebinar: React.FC<
  UhOhProps & {
    webinarId?: string;
    teamId?: string;
    extensions?: {
      code?: 'NOT_FOUND' | 'NO_SESSION' | 'NOT_PUBLISHED' | 'SUSPENDED';
      extensions?: ErrorExtensions;
    };
  }
> = ({ extensions, teamId, webinarId }) => {
  const me = useMe();

  useRunOnce(() => {
    (async () => {
      if (me) return;

      let path = 'webinar-not-available';

      switch (extensions?.code) {
        case 'NOT_FOUND':
        case 'NOT_PUBLISHED':
          // Team is ok but this set isn't available
          path = 'webinar-not-available';
          break;

        // No set found - we probably don't know what team this relates to
        case 'SUSPENDED':
          path = 'account-not-available';
          break;
      }

      const extObject = extensions?.extensions as Record<string, string>;
      let params: string[] = [];

      if (extObject) {
        params = Object.keys(extObject).map((key) => {
          return `${key}=${encodeURIComponent(extObject[key])}`;
        });
      }

      if (teamId) {
        params.push(`t=${teamId}`);
      }

      if (webinarId) {
        params.push(`w=${webinarId}`);
      }

      const visitorId = getVisitorId();
      if (visitorId) {
        params.push(`vid=${visitorId}`);
      }

      await logger.error(`NOT_FOUND ${path}`, { webinarId, teamId, extensions, visitorId });
      await logger.sendLogs();

      location.href = `https://ewebinar.com/${path}${params.length > 0 ? '?' : ''}${params.join(
        '&'
      )}`;
    })();
  });

  if (me) {
    return (
      <UhOhPage>
        <SEO title='Hmmm, where did it go?' />

        <Header />

        <UhOh.Container>
          <UhOh.Title>Webinar not found...</UhOh.Title>
          <UhOh.Body>The webinar you're looking for is no longer available.</UhOh.Body>
        </UhOh.Container>
      </UhOhPage>
    );
  }

  return null;
};

export default NoWebinar;
